<script setup lang="ts">
import { computed, ref } from 'vue';
import Badge from 'primevue/badge';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Popover from 'primevue/popover';
import type { OpportunityType, User } from '@apparatix/types/Leads';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { useOpportunities, useStringFunctions } from '@apparatix/composables';
import SelectButtonGroup from '@apparatix/library/SelectButtonGroup.vue';
import FilterFillIcon from '@primeicons/vue/filter-fill';
import AngleDownIcon from '@primeicons/vue/angle-down';
import SearchIcon from '@primeicons/vue/search';
import CheckIcon from '@primeicons/vue/check';

const props = defineProps<{ type: OpportunityType }>();

const searchQuery = ref('');
const overlayRef = ref();
const selectedTabButtonItem = ref(0);

const store = useOpportunityStore();
const { addOrRemoveOwnerFromFilterGroup, isOwnerSelectedForFilter } = useOpportunities();
const { isEmptyString } = useStringFunctions();

const filteredOwners = computed(() => {
  if (isEmptyString(searchQuery.value, true)) {
    return store.usersWithOpportunities[props.type];
  }

  return store.usersWithOpportunities[props.type].filter(user => user.name.toLowerCase().includes(searchQuery.value.trim().toLowerCase()));
});

const toggle = (e: MouseEvent) => overlayRef.value.toggle(e);

const onOwnerClick = (e: MouseEvent, owner: User) => {
  addOrRemoveOwnerFromFilterGroup(props.type, owner);
  toggle(e);
};
</script>
<template>
  <div class="flex gap-2 items-center justify-center p-2 rounded-lg bg-[var(--light-neutral-background)] cursor-pointer" @click="toggle">
    <FilterFillIcon size="0.875rem" color="var(--icon-button-color)" />
    <AngleDownIcon size="0.875rem" color="var(--icon-button-color)" />
  </div>
  <Popover
    ref="overlayRef"
    unstyled
    :pt="{
      root: {
        style: {
          boxShadow: 'var(--popover-box-shadow)',
        },
        class: 'rounded-lg bg-transparent'
      },
      content: {
        class: 'rounded-lg bg-transparent'
      }
    }"
  >
    <div class="flex flex-col max-h-[50vh] lg:max-h-[66vh] lg:min-h-[33vh] max-h-screen rounded-lg border border-[var(--popover-border-color)] overflow-hidden bg-[var(--white)]">
      <div class="flex-none flex flex-col gap-2 p-3 bg-[var(--light-neutral-background)]">
        <div class="py-3">
          <h1 class="font-semibold leading-none text-[var(--swimlane-primary-text)]">
            {{ props.type === 'Opportunity' ? 'Opportunities' : 'Leads' }} Filters
          </h1>
        </div>
        <div class="w-full">
          <IconField icon-position="left">
            <InputIcon>
              <SearchIcon />
            </InputIcon>
            <InputText v-model="searchQuery" placeholder="Search owner" />
          </IconField>
        </div>
      </div>
      <div class="flex-none flex flex-col gap-1 py-2 px-3 border-t border-[var(--popover-border-color)]">
        <div class="py-3">
          <p class="text-xs font-semibold uppercase text-[var(--icon-button-color)]">
            All filters
          </p>
        </div>
        <SelectButtonGroup :items="[{label: 'Owners'}]" :active-item="selectedTabButtonItem" @on:item-select="item => selectedTabButtonItem = item" />
      </div>
      <div v-if="selectedTabButtonItem === 0" class="flex-1 overflow-y-auto flex flex-col gap-2 px-3">
        <div v-for="user in filteredOwners" :key="user.id">
          <div class="flex gap-2 items-center">
            <CheckIcon v-if="isOwnerSelectedForFilter(props.type, user)" />
            <span v-else>{{ ' ' }}</span>
            <span class="cursor-pointer flex items-center gap-2" @click="e => onOwnerClick(e, user)">{{ user.name }} <Badge :value="user.opportunityCount" severity="secondary" /></span>
          </div>
        </div>
      </div>
    </div>
  </Popover>
</template>
